import React from 'react';
import { Box, Button, Grid, RadioGroup } from '@mui/material';
import type { FormikValues, useFormik } from 'formik';
import { PAGE_TEXT } from '../lang';
import { Accordion, Checkbox, Radio } from '../../../../../components';
import { type ApplicationDeclarationData, PERSONAL_INFO_TYPE } from './types';

type Formik<T extends FormikValues> = ReturnType<typeof useFormik<T>>;
type DeclarationProps = {
  formik: Formik<ApplicationDeclarationData>;
  enableEdit: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void; // show in-component 'save & submit' button when valid.
};

const DECLARATION = PAGE_TEXT.DECLARATION;

export const ApplicationDeclaration: React.FunctionComponent<
  DeclarationProps
> = props => {
  const { formik, enableEdit, onClick } = props;

  const handleChangePersonalInfoType = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setFieldValue('personalType', event.target.value);
  };

  const handleChangeEngagement = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    await formik.setFieldValue('engagement', event.target.value === 'true');
  };

  const personalInfoCollectGroup = [
    {
      label: DECLARATION.MAINLAND_INFO,
      value: PERSONAL_INFO_TYPE.CN,
    },
    {
      label: DECLARATION.EU_INFO,
      value: PERSONAL_INFO_TYPE.EU,
    },
    {
      label: DECLARATION.OTHERS_INFO,
      value: PERSONAL_INFO_TYPE.OTHERS,
    },
  ];

  return (
    <Accordion
      style={{ width: '100%' }}
      title={DECLARATION.TITLE}
      defaultExpanded
      details={
        <Grid
          container
          spacing={2}
          direction={'column'}
          sx={{
            padding: '2rem',
          }}
        >
          <Checkbox
            name="informationAccuracy"
            value={formik.values.informationAccuracy}
            checked={formik.values.informationAccuracy}
            label={
              <div className="!mt-2">{DECLARATION.INFORMATION_ACCURATE}</div>
            }
            error={
              formik.touched.informationAccuracy &&
              Boolean(formik.errors.informationAccuracy)
            }
            disabled={!enableEdit}
            sx={{
              alignItems: 'flex-start',
            }}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <Checkbox
            name="confirmation"
            value={formik.values.confirmation}
            checked={formik.values.confirmation}
            label={<div className="!mt-2">{DECLARATION.CONFIRMATION}</div>}
            error={
              formik.touched.confirmation && Boolean(formik.errors.confirmation)
            }
            disabled={!enableEdit}
            sx={{
              alignItems: 'flex-start',
            }}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          <RadioGroup
            name="personalType"
            value={formik.values.personalType}
            onChange={handleChangePersonalInfoType}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              margin: '0 0 0.25rem 2rem',
            }}
          >
            {personalInfoCollectGroup.map((item, index) => {
              return (
                <Radio
                  key={index}
                  value={item.value}
                  label={
                    <div
                      className="!mt-2"
                      dangerouslySetInnerHTML={{
                        __html: item.label,
                      }}
                    />
                  }
                  checked={formik.values.personalType === item.value}
                  disabled={!enableEdit}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: 18,
                    },
                    alignItems: 'flex-start',
                  }}
                  error={
                    formik.touched.personalType &&
                    Boolean(formik.errors.personalType)
                  }
                />
              );
            })}
          </RadioGroup>
          <div
            className="w-full mt-[1.5rem]"
            style={{
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
            }}
          >
            {DECLARATION.ENGAGEMENT}
            <RadioGroup
              name="engagement"
              value={formik.values.engagement}
              onChange={handleChangeEngagement}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '2rem',
                margin: '0.5rem 0 0 0.5rem',
              }}
            >
              <Radio
                value={true}
                label="yes"
                checked={formik.values.engagement === true}
                disabled={!enableEdit}
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 18,
                  },
                }}
                error={
                  formik.touched.engagement && Boolean(formik.errors.engagement)
                }
              />
              <Radio
                value={false}
                label="no"
                checked={formik.values.engagement === false}
                disabled={!enableEdit}
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 18,
                  },
                }}
                error={
                  formik.touched.engagement && Boolean(formik.errors.engagement)
                }
              />
            </RadioGroup>
          </div>

          {onClick && (
            <Box display={'flex'} justifyContent={'center'} marginTop={2}>
              <Button color="primary" variant="contained" onClick={onClick}>
                {PAGE_TEXT.MAKE_UP_DECLARATION.SAVE_AND_SUBMIT}
              </Button>
            </Box>
          )}
        </Grid>
      }
    />
  );
};
